/**
 * Route table for the referee flow. The tokenized link `/r/:token` is the session; the index
 * resolves the referee's furthest incomplete step and each step is wrapped in StepRoute (loading /
 * error / guard / shell). Exported as a plain array so tests can mount it under a memory router.
 *
 * `/never-contact` and `/unsubscribe` are the exceptions to "the link is the session": they carry a
 * DIFFERENT token (a signed suppression token, in `?token=`) and resolve no session at all. They are
 * mounted here because the referee invite footer stamps that URL into live outbound mail — the path
 * segment must match `ReferenceRequestOptions.NeverContactBaseUrl`, or a legally required opt-out
 * link resolves to the NotFound wall.
 */
import { Navigate, type RouteObject } from 'react-router-dom';
import { StepRoute } from '@/session/StepRoute';
import { TokenEntry } from '@/session/TokenEntry';
import { Landing } from '@/routes/Landing';
import { Confirm } from '@/routes/Confirm';
import { Record } from '@/routes/Record';
import { Processing } from '@/routes/Processing';
import { Review } from '@/routes/Review';
import { Sign } from '@/routes/Sign';
import { Done } from '@/routes/Done';
import { RootNotice } from '@/routes/RootNotice';
import { NeverContactRoute, UnsubscribeRoute } from '@/routes/OptOut';
import {
  DeclinedRoute,
  ExpiredRoute,
  NotFoundRoute,
  UnderReviewRoute,
  WithdrawnRoute,
} from '@/routes/states';

export const routes: RouteObject[] = [
  { path: '/', element: <RootNotice /> },
  {
    path: '/r/:token',
    children: [
      { index: true, element: <TokenEntry /> },
      { path: 'landing', element: <StepRoute step="landing"><Landing /></StepRoute> },
      { path: 'confirm', element: <StepRoute step="confirm"><Confirm /></StepRoute> },
      { path: 'record', element: <StepRoute step="record"><Record /></StepRoute> },
      { path: 'processing', element: <StepRoute step="processing"><Processing /></StepRoute> },
      { path: 'review', element: <StepRoute step="review"><Review /></StepRoute> },
      { path: 'sign', element: <StepRoute step="sign"><Sign /></StepRoute> },
      { path: 'done', element: <StepRoute step="done"><Done /></StepRoute> },
      { path: 'declined', element: <StepRoute step="declined"><DeclinedRoute /></StepRoute> },
      { path: 'expired', element: <StepRoute step="expired"><ExpiredRoute /></StepRoute> },
      { path: 'withdrawn', element: <StepRoute step="withdrawn"><WithdrawnRoute /></StepRoute> },
      {
        path: 'under-review',
        element: <StepRoute step="under-review"><UnderReviewRoute /></StepRoute>,
      },
      // Unknown sub-path under a token → back to the index, which restores the correct step.
      { path: '*', element: <Navigate to=".." relative="path" replace /> },
    ],
  },
  // The public opt-out pages (WP-B6). Sessionless and token-in-query, so they sit OUTSIDE the
  // `/r/:token` subtree — and above the catch-all, which would otherwise swallow them.
  { path: '/never-contact', element: <NeverContactRoute /> },
  { path: '/unsubscribe', element: <UnsubscribeRoute /> },
  { path: '*', element: <NotFoundRoute /> },
];
